import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Image,
  ListGroup,
  Card,
  Button,
  ButtonGroup,
  Form,
  Carousel,
  Modal,
  Table,
  InputGroup,
  Badge,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Loader from "../Components/Loader";
import Message from "../Components/Message";
import Meta from "../Components/Meta";
import { breadcrumbsFor, breadcrumbSchema, isIndexableProduct } from '../seo/policy';
import { buildProductSummary } from '../seo/productSummary';
import RelatedProductsCarousel from "../Components/RelatedProductsCarousel";
import ProductLocationsSummary from "../Components/admin/ProductLocationsSummary";

import {
  useGetProductDetailsQuery,
  useGetRelatedProductsQuery,
} from "../slices/productsApiSlice";
import { addToCart } from "../slices/cartSlice";
import { loadGarageVehicle } from "../utils/garage";
import { fitsVehicle } from "../utils/fitment";
import PriceWithSale from "../Components/PriceWithSale";
import { getProductDisplayName } from "../utils/productTitle";
import { getEffectivePrice } from "../utils/salePricing";
import { formatProductDimension } from "../utils/productDimension";
import { buildProductSeo } from "../utils/productSeo";
import { isProductPurchasable } from "../utils/productStatus";
import { isBrakePadProduct, isBrakeRotorProduct, isNonCoated } from "../utils/brakeParts";
import { getProductBackDestination } from "../utils/navigation";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const normalizeFitmentText = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[-_/.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const tokenizeFitmentText = (s) =>
  normalizeFitmentText(s).split(" ").filter(Boolean);

const scoreFitmentRow = (row, tokens) => {
  if (!tokens.length) return 1;

  const make = normalizeFitmentText(row.make);
  const model = normalizeFitmentText(row.model);
  const trim = normalizeFitmentText(row.trim);
  const engine = normalizeFitmentText(row.engine);
  const year = row.year != null ? String(row.year) : "";

  const fields = [make, model, trim, engine, year];

  let score = 0;

  for (const t of tokens) {
    const isYearToken = /^\d{4}$/.test(t);
    let matched = false;

    if (isYearToken && year === t) {
      score += 8;
      matched = true;
    }

    if (!matched) {
      for (const f of fields) {
        if (!f) continue;

        if (f === t) {
          score += 6;
          matched = true;
          break;
        }
        if (f.startsWith(t)) {
          score += 4;
          matched = true;
          break;
        }
        if (f.includes(t)) {
          score += 2;
          matched = true;
          break;
        }
      }
    }

    if (!matched) return 0;
  }

  const hasMakeToken = tokens.some((t) => make.includes(t));
  const hasModelToken = tokens.some((t) => model.includes(t));
  if (hasMakeToken && hasModelToken) score += 2;

  return score;
};

const ProductScreen = () => {
  const LIMIT = 10;

  const { id: productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Qty selector
  const [qty, setQty] = useState(1);

  // Fitments UI
  const [fitmentSearch, setFitmentSearch] = useState("");
  const [fitmentView, setFitmentView] = useState("fitment");

  // Add-to-cart modal
  const [showCartModal, setShowCartModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [addedQty, setAddedQty] = useState(1);

  // Image magnifier state (overlay panel appears on hover only; layout does NOT change)
  const [isHoveringImage, setIsHoveringImage] = useState(false);
  const [activeImage, setActiveImage] = useState("");
  const [cursorPct, setCursorPct] = useState({ x: 50, y: 50 }); // 0..100
  const zoom = 2.6;

  const { userInfo } = useSelector((state) => state.auth);
  const isTieredUser = Boolean(userInfo?.tier?.id);
  const { cartItems, itemsPrice } = useSelector((state) => state.cart);

  const lastToastIdRef = useRef(null);
  const cartPreview = useMemo(() => {
    const items = Array.isArray(cartItems) ? cartItems : [];
    const getUnitPrice = (item) => {
      if (item?.tierPriceApplied || item?.ignoreSale) {
        return Number(item?.price ?? 0);
      }
      return getEffectivePrice({
        price: Number(item?.originalPrice ?? item?.price ?? 0),
        salePrice: item?.salePrice,
        saleStartAt: item?.saleStartAt,
        saleEndAt: item?.saleEndAt,
      });
    };
    const totalQty = items.reduce((sum, item) => sum + Number(item.qty || 0), 0);
    const totalPrice = items.reduce(
      (sum, item) => sum + Number(item.qty || 0) * getUnitPrice(item),
      0
    );
    const previewItems = items.slice(0, 3);
    return {
      previewItems,
      remainingCount: Math.max(items.length - previewItems.length, 0),
      totalQty,
      totalPrice,
      itemsPrice: Number(itemsPrice || 0),
    };
  }, [cartItems, itemsPrice]);

  const getCartItemUnitPrice = (item) => {
    if (item?.tierPriceApplied || item?.ignoreSale) {
      return Number(item?.price ?? 0);
    }
    return getEffectivePrice({
      price: Number(item?.originalPrice ?? item?.price ?? 0),
      salePrice: item?.salePrice,
      saleStartAt: item?.saleStartAt,
      saleEndAt: item?.saleEndAt,
    });
  };

  // Keep garage vehicle in state so it updates after modal save
  const [veh, setVeh] = useState(() =>
    isTieredUser ? null : loadGarageVehicle()
  );

  useEffect(() => {
    if (isTieredUser) {
      setVeh(null);
      return;
    }
    const refresh = () => setVeh(loadGarageVehicle());
    window.addEventListener("garage:updated", refresh);
    return () => window.removeEventListener("garage:updated", refresh);
  }, [isTieredUser]);

  const {
    data: dataProduct,
    isLoading,
    error,
  } = useGetProductDetailsQuery(productId);

  const exactVehicle = useMemo(() => {
    const selectedVehicle = location.state?.vehicleContext || veh;
    if (!selectedVehicle?.make || !selectedVehicle?.model || !selectedVehicle?.year) {
      return undefined;
    }
    return {
      make: selectedVehicle.make,
      model: selectedVehicle.model,
      year: selectedVehicle.year,
      ...(selectedVehicle.trim ? { trim: selectedVehicle.trim } : {}),
    };
  }, [location.state, veh]);

  const {
    data: relatedProductsData,
    isLoading: isLoadingRelated,
    error: relatedProductsError,
  } = useGetRelatedProductsQuery({ productId, vehicle: exactVehicle });
  const relatedProducts = relatedProductsData?.products || [];
  const relatedProductsTitle = relatedProductsData?.mode === "related" || (!relatedProductsData && exactVehicle)
    ? "Related Products"
    : "More products in this category";

  // Safe product object
  const product = {
    reviews: [],
    oemNumbers: [],
    fitments: [],
    interchangeables: [],
    stockQty: 0,
    price: 0,
    ...dataProduct,
  };

  const displayName = useMemo(() => getProductDisplayName(product), [product]);
  const isPurchasable = isProductPurchasable(product);
  const isComingSoon = product.status === 'comingSoon';
  const isDraft = product.status === 'draft';
  const productSeo = useMemo(
    () => buildProductSeo({ ...product, displayName }, productId),
    [product, displayName, productId]
  );
  const productSummary = useMemo(() => buildProductSummary(product), [product]);
  const isEngineOil = String(product.category || '').toLowerCase() === 'engine-oil';
  const productAttributes = Object.entries(product.attributes || {}).filter(
    ([, value]) => String(value || '').trim()
  );
  const tierPrice =
    isTieredUser && product?.tierPrice != null
      ? Number(product.tierPrice)
      : null;

  // Images (keep multi-image behavior)
  const images =
    product.images && product.images.length
      ? product.images
      : product.image
      ? [product.image]
      : [];

  // Ensure activeImage always valid
  useEffect(() => {
    if (!activeImage && images.length) setActiveImage(images[0]);
  }, [images, activeImage]);

  // Open Garage modal ONLY when user clicks (Header listens to this)
  const openGarageModal = (focus = "trim") => {
    if (isTieredUser) return;
    window.dispatchEvent(new CustomEvent("garage:open", { detail: { focus } }));
  };

  // Strict: require make/model/year/trim to claim fitment
  const missingFields = useMemo(() => {
    const missing = [];
    if (!veh) return missing;
    if (!veh.make) missing.push("make");
    if (!veh.model) missing.push("model");
    if (!veh.year) missing.push("year");
    if (!veh.trim) missing.push("trim");
    return missing;
  }, [veh]);

  const isGarageComplete = !!veh && missingFields.length === 0;
  const firstMissing = missingFields[0] || "trim";

  // Only check fitment when garage is complete
  const fit = isGarageComplete ? fitsVehicle(product, veh) : null;

  // Specs
  const productDimension = formatProductDimension(
    product.productDimension,
    product.productDimensionUnit || "mm"
  ) || null;
  const packageDimension = product.packageDimension
    ? `${product.packageDimension} ${product.packageDimensionUnit || "cm"}`
    : null;
  const packageWeight = product.packageWeight
    ? `${product.packageWeight} ${product.packageWeightUnit || "kg"}`
    : null;


  const hasValue = (v) => !(v === '' || v === null || v === undefined);
  const isPositive = (v) => Number(v) > 0;
  const hasText = (v) => String(v || "").trim().length > 0;
  const isMeaningfulText = (v) => {
    const text = String(v || "").trim();
    if (!text) return false;
    const normalized = text.toLowerCase();
    return normalized !== "none" && normalized !== "n/a";
  };
  const effectiveBrakeSpecType = product?.brakeSpecType === "pad" || product?.brakeSpecType === "rotor" || product?.brakeSpecType === "none"
    ? product.brakeSpecType
    : isBrakePadProduct(product) ? "pad" : isBrakeRotorProduct(product) ? "rotor" : "none";
  const rotorDiameterText = (() => {
    const mm = product?.brakeRotorSpecs?.outerDiameterMm;
    const inch = product?.brakeRotorSpecs?.outerDiameterIn;
    if (isPositive(mm) && isPositive(inch)) return `${mm} mm / ${inch} in`;
    if (isPositive(mm)) return `${mm} mm`;
    if (isPositive(inch)) return `${inch} in`;
    return '';
  })();

  const rotorSizeText = String(product?.brakeRotorSpecs?.rotorSizeLabel || '').trim();
  const rotorCoatingType = product?.brakeRotorSpecs?.coatingType === 'Other'
    ? (product?.brakeRotorSpecs?.coatingTypeOther || '')
    : (product?.brakeRotorSpecs?.coatingType || '');
  const showRotorSize = hasText(rotorSizeText) && rotorSizeText !== rotorDiameterText;
  const nonCoatedProduct = isNonCoated(product?.coating);
  const hasRotorStructuredMeasurements = Boolean(
    isPositive(product?.brakeRotorSpecs?.outerDiameterMm) ||
    isPositive(product?.brakeRotorSpecs?.overallHeightMm) ||
    isPositive(product?.brakeRotorSpecs?.nominalThicknessMm) ||
    isPositive(product?.brakeRotorSpecs?.minimumThicknessMm) ||
    isPositive(product?.brakeRotorSpecs?.centerBoreMm) ||
    isPositive(product?.brakeRotorSpecs?.boltCircleDiameterMm)
  );
  const legacyPadSizeFallback = hasText(product?.brakePadSpecs?.manufacturerPadSizeText)
    ? `${String(product.brakePadSpecs.manufacturerPadSizeText).replace(/\*/g, " × ")} mm`
    : "";
  const hasPadStructuredMeasurements = Boolean(hasText(legacyPadSizeFallback));
  const showGeneralProductDimension =
    hasText(productDimension) &&
    !(
      (effectiveBrakeSpecType === "rotor" && hasRotorStructuredMeasurements) ||
      (effectiveBrakeSpecType === "pad" && hasPadStructuredMeasurements)
    );

  const brakeSpecs = [
    effectiveBrakeSpecType === "rotor" && rotorDiameterText && { label: 'Outer Diameter', value: rotorDiameterText },
    effectiveBrakeSpecType === "rotor" && isPositive(product?.brakeRotorSpecs?.overallHeightMm) && { label: 'Overall Height', value: `${product.brakeRotorSpecs.overallHeightMm} mm` },
    effectiveBrakeSpecType === "rotor" && isPositive(product?.brakeRotorSpecs?.nominalThicknessMm) && { label: 'Nominal Thickness', value: `${product.brakeRotorSpecs.nominalThicknessMm} mm` },
    effectiveBrakeSpecType === "rotor" && isPositive(product?.brakeRotorSpecs?.minimumThicknessMm) && { label: 'Minimum Thickness', value: `${product.brakeRotorSpecs.minimumThicknessMm} mm` },
    effectiveBrakeSpecType === "rotor" && isPositive(product?.brakeRotorSpecs?.centerBoreMm) && { label: 'Center Bore / Hub Bore', value: `${product.brakeRotorSpecs.centerBoreMm} mm` },
    effectiveBrakeSpecType === "rotor" && isPositive(product?.brakeRotorSpecs?.boltCircleDiameterMm) && { label: 'Bolt Circle Diameter', value: `${product.brakeRotorSpecs.boltCircleDiameterMm} mm` },
    effectiveBrakeSpecType === "rotor" && !nonCoatedProduct && hasValue(product?.brakeRotorSpecs?.rustResistantCoated) && { label: 'Rust Resistant Coated', value: product.brakeRotorSpecs.rustResistantCoated },
    effectiveBrakeSpecType === "rotor" && !nonCoatedProduct && hasValue(rotorCoatingType) && { label: 'Coating Type', value: rotorCoatingType },
    effectiveBrakeSpecType === "rotor" && isPositive(product?.brakeRotorSpecs?.studLugHoleQuantity) && { label: 'Stud/Lug Holes', value: product.brakeRotorSpecs.studLugHoleQuantity },
    effectiveBrakeSpecType === "rotor" && hasValue(product?.brakeRotorSpecs?.rotorConstruction) && { label: 'Rotor Construction', value: product.brakeRotorSpecs.rotorConstruction },
    effectiveBrakeSpecType === "rotor" && hasValue(product?.brakeRotorSpecs?.drivetrainType) && { label: 'Drivetrain', value: product.brakeRotorSpecs.drivetrainType },
    effectiveBrakeSpecType === "rotor" && showRotorSize && { label: 'Rotor Size', value: rotorSizeText },
    effectiveBrakeSpecType === "pad" && hasValue(product?.brakePadSpecs?.drivetrainType) && { label: 'Drivetrain', value: product.brakePadSpecs.drivetrainType },
    effectiveBrakeSpecType === "pad" && hasValue(product?.brakePadSpecs?.padWearSensorIncluded) && { label: 'Pad Wear Sensor Included', value: product.brakePadSpecs.padWearSensorIncluded },
    effectiveBrakeSpecType === "pad" && hasValue(product?.brakePadSpecs?.mountingHardwareIncluded) && { label: 'Mounting Hardware Included', value: product.brakePadSpecs.mountingHardwareIncluded },
    effectiveBrakeSpecType === "pad" && hasValue(product?.brakePadSpecs?.brakeLubricantIncluded) && { label: 'Brake Lubricant Included', value: product.brakePadSpecs.brakeLubricantIncluded },
    effectiveBrakeSpecType === "pad" && !hasText(productDimension) && hasText(legacyPadSizeFallback) && { label: 'Pad Size', value: legacyPadSizeFallback },
  ].filter(Boolean);

  const specs = [
    isEngineOil && product.brand && { label: "Brand", value: product.brand },
    !isEngineOil && product.position && { label: "Position", value: product.position },
    product.partNumber && {
      label: isEngineOil ? "Part Number / Oil Grade" : "Part number (OEM)",
      value: product.partNumber,
    },
    packageDimension && { label: "Package Dimension", value: packageDimension },
    !isEngineOil && showGeneralProductDimension && { label: "Product Dimension", value: productDimension },
    packageWeight && { label: "Package Weight", value: packageWeight },
    product.sku && { label: "SKU", value: product.sku },
    !isEngineOil && isMeaningfulText(product?.material) && { label: "Material", value: product.material },
    !isEngineOil && hasText(product?.coating) && { label: "Coating", value: product.coating },
    ...(!isEngineOil ? brakeSpecs : []),
  ].filter(Boolean);

  const interchangeablesData = useMemo(() => {
    if (Array.isArray(product.interchangeables)) return product.interchangeables;
    if (Array.isArray(product.interchangeable)) return product.interchangeable;
    if (Array.isArray(product.interchangables)) return product.interchangables;
    if (Array.isArray(product.interchangable)) return product.interchangable;
    return [];
  }, [
    product.interchangeables,
    product.interchangeable,
    product.interchangables,
    product.interchangable,
  ]);

  const hasFitments = Array.isArray(product.fitments) && product.fitments.length > 0;
  const hasInterchangeables = interchangeablesData.length > 0;

  useEffect(() => {
    if (hasFitments) {
      setFitmentView("fitment");
      return;
    }
    if (hasInterchangeables) {
      setFitmentView("interchangeable");
    }
  }, [hasFitments, hasInterchangeables]);

  const goToCart = () => {
    setShowCartModal(false);
    navigate("/cart");
  };

  const addToCartHandler = () => {
    if (!isPurchasable) {
      toast.error('This product is not available for purchase yet.');
      return;
    }
    const basePrice = Number(product.price);
    const tierPrice =
      isTieredUser && product?.tierPrice != null
        ? Number(product.tierPrice)
        : null;
    const effectivePrice = isTieredUser
      ? tierPrice ?? basePrice
      : getEffectivePrice({
          price: basePrice,
          salePrice: product.salePrice,
          saleStartAt: product.saleStartAt,
          saleEndAt: product.saleEndAt,
        });
    const tierPriceApplied = Boolean(isTieredUser && tierPrice != null);

    const key = product._id;
    const existing = cartItems?.find((x) => (x._id || x.product) === key);
    const currentQty = Number(existing?.qty ?? 0);

    const minQty = 1;
    const maxPerItem = isTieredUser ? Number.POSITIVE_INFINITY : LIMIT;
    const requestedQty = Math.max(minQty, Number(qty ?? minQty));
    const remaining = maxPerItem - currentQty;

    if (lastToastIdRef.current != null) toast.dismiss(lastToastIdRef.current);

    if (!isTieredUser && remaining <= 0) {
      lastToastIdRef.current = toast.info("Limit reached: max 10 per item.");
      setShowLimitModal(true);
      return;
    }

    const allowedAdd = isTieredUser
      ? requestedQty
      : Math.min(requestedQty, remaining);

    dispatch(
      addToCart({
        ...product,
        name: displayName || product.name,
        displayName,
        qty: allowedAdd, // reducer increments
        price: effectivePrice,
        originalPrice: basePrice,
        salePrice: product.salePrice != null ? Number(product.salePrice) : 0,
        saleStartAt: product.saleStartAt,
        saleEndAt: product.saleEndAt,
        tierPrice: tierPriceApplied ? tierPrice : null,
        tierPriceApplied,
        ignoreSale: isTieredUser,
      })
    );

    setAddedQty(allowedAdd);
    setShowCartModal(true);
    if (!isTieredUser && requestedQty > remaining) {
      setShowLimitModal(true);
    }

    const newQty = currentQty + allowedAdd;
    if (!isTieredUser && newQty >= LIMIT) {
      lastToastIdRef.current = toast.warning("Reached max 10 per item.");
    }
  };

  // Qty dropdown should not exceed LIMIT or stock
  const maxSelectableQty = isTieredUser
    ? Number(product.stockQty ?? 0) || 0
    : Math.min(LIMIT, Number(product.stockQty ?? 0) || 0);
  const minSelectableQty = 1;
  const qtyOptions =
    maxSelectableQty > 0
      ? Array.from(
          { length: Math.max(0, maxSelectableQty - minSelectableQty + 1) },
          (_, idx) => minSelectableQty + idx
        )
      : [];

  // Fitments rows
  const fitmentRows = useMemo(() => {
    const arr = Array.isArray(product.fitments) ? product.fitments : [];
    return arr.map((f, idx) => {
      const make = (f.make || "").trim();
      const model = (f.model || "").trim();
      const year =
        f.year ?? f.fromYear ?? f.yearFrom ?? f.yearStart ?? f.yearEnd ?? null;
      const trim = (f.trim || f.submodel || "").trim();
      const engine = (f.engine || "").trim();

      return {
        key: `${make}-${model}-${year ?? ""}-${trim}-${engine}-${idx}`,
        make,
        model,
        year,
        trim,
        engine,
      };
    });
  }, [product.fitments]);

  const interchangeableRows = useMemo(() => {
    return interchangeablesData.map((entry, idx) => ({
      key: `${entry.brandLabel || ""}-${entry.partNumber || ""}-${idx}`,
      brandLabel: entry.brandLabel || "",
      partNumber: entry.partNumber || "",
    }));
  }, [interchangeablesData]);

  const filteredFitments = useMemo(() => {
    const tokens = tokenizeFitmentText(fitmentSearch);
    if (!tokens.length) return fitmentRows;

    return fitmentRows
      .map((r) => ({ r, s: scoreFitmentRow(r, tokens) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.r);
  }, [fitmentRows, fitmentSearch]);

  // ----- Magnifier overlay handlers -----
  const handleImageEnter = (img) => {
    setActiveImage(img);
    setIsHoveringImage(true);
  };

  const handleImageLeave = () => {
    setIsHoveringImage(false);
  };

  const handleImageMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
    const y = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);
    setCursorPct({ x, y });
  };

  const handleBack = () => {
    navigate(getProductBackDestination(location, product), { replace: true });
  };

  return (
    <>
      <Button onClick={handleBack} variant="light" className="btn btn-light my-3">
        Go Back
      </Button>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || error.error}</Message>
      ) : (
        <>
          <Meta
            title={productSeo.title}
            description={productSeo.description}
            canonicalPath={productSeo.canonicalPath}
            noindex={!isIndexableProduct(product)}
            jsonLd={isDraft ? undefined : [productSeo.jsonLd, breadcrumbSchema(breadcrumbsFor(product))]}
          />

          <Row className="g-4">
            <Col md={5}>
              {images.length === 0 ? (
                <div className="border rounded p-5 text-muted text-center">
                  No image
                </div>
              ) : (
                <div className="product-img-overlay-wrap">
                  {images.length === 1 ? (
                    <div
                      className="product-img-stage"
                      onMouseEnter={(e) => {
                        handleImageEnter(images[0]);
                        handleImageMove(e);
                      }}
                      onMouseMove={handleImageMove}
                      onMouseLeave={handleImageLeave}
                    >
                      <Image src={images[0]} alt={displayName} fluid />
                      <div className="small text-muted mt-2">Hover to zoom</div>
                    </div>
                  ) : (
                    <Carousel variant="dark">
                      {images.map((img, idx) => (
                        <Carousel.Item key={idx}>
                          <div
                            className="product-img-stage"
                            onMouseEnter={(e) => {
                              handleImageEnter(img);
                              handleImageMove(e);
                            }}
                            onMouseMove={handleImageMove}
                            onMouseLeave={handleImageLeave}
                          >
                            <Image
                              src={img}
                              alt={`${displayName} ${idx + 1}`}
                              fluid
                            />
                            <div className="small text-muted mt-2">
                              Hover to zoom
                            </div>
                          </div>
                        </Carousel.Item>
                      ))}
                    </Carousel>
                  )}

                  {isHoveringImage && activeImage && (
                    <div className="product-zoom-overlay">
                      <div
                        className="product-zoom-overlay-inner"
                        style={{
                          backgroundImage: `url(${activeImage})`,
                          backgroundPosition: `${cursorPct.x}% ${cursorPct.y}%`,
                          backgroundSize: `${zoom * 100}%`,
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </Col>

            <Col md={4}>
              <Card className="card-plain">
                <Card.Body>
                  <div className="d-flex flex-column gap-2">
                    <div>
                      <h1 className="h3 mb-1">{displayName}</h1>
                      {isComingSoon && (
                        <Badge bg="warning" text="dark" className="mt-2">Coming Soon</Badge>
                      )}
                    </div>

                    <div className="text-muted">
                      {product.description ? product.description : "—"}
                    </div>

                    {specs.length > 0 && (
                      <div className="mt-2">
                        <h6 className="mb-2">Specs</h6>
                        <ul className="mb-0 ps-3">
                          {specs.map((spec) => (
                            <li key={spec.label}>
                              <strong>{spec.label}:</strong> {spec.value}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="shadow-sm">
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Price</span>
                      <span>
                        {isComingSoon ? (
                          <strong>Price coming soon</strong>
                        ) : (
                          <PriceWithSale
                          price={Number(product.price)}
                          salePrice={
                            product.salePrice != null
                              ? Number(product.salePrice)
                              : null
                          }
                          saleStartAt={product.saleStartAt}
                          saleEndAt={product.saleEndAt}
                          tierPrice={tierPrice}
                          showTierTag={Boolean(tierPrice)}
                          forceBasePrice={isTieredUser && !tierPrice}
                          />
                        )}
                      </span>
                    </div>
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Status</span>
                      <strong>
                        {isComingSoon
                          ? "Coming Soon"
                          : product.stockQty > 0 ? "In Stock" : "Out Of Stock"}
                      </strong>
                    </div>
                  </ListGroup.Item>

                  {veh && !isEngineOil && (
                    <ListGroup.Item>
                      {!isGarageComplete ? (
                        <div>
                          <small className="text-warning fw-semibold d-block">
                            ⚠️ To confirm fitment 100%, please complete your garage
                            vehicle:{" "}
                            <strong>{missingFields.join(", ")}</strong>.
                          </small>

                          <Button
                            variant="link"
                            className="p-0 mt-1"
                            onClick={() => openGarageModal(firstMissing)}
                          >
                            Update your garage vehicle
                          </Button>
                        </div>
                      ) : (
                        <>
                          {fit === true && (
                            <small className="text-success fw-semibold">
                              ✅ Fits your {veh.year} {veh.make} {veh.model}{" "}
                              {veh.trim}
                            </small>
                          )}

                          {fit === false && (
                            <small className="text-danger fw-semibold">
                              ⚠️ Does NOT fit your {veh.year} {veh.make}{" "}
                              {veh.model} {veh.trim}
                            </small>
                          )}

                          {fit === null && (
                            <small className="text-muted d-block">
                              We couldn't verify fitment for your {veh.year}{" "}
                              {veh.make} {veh.model} {veh.trim}.
                            </small>
                          )}
                        </>
                      )}
                    </ListGroup.Item>
                  )}

                  {isPurchasable && product.stockQty > 0 && (
                    <ListGroup.Item>
                      <Row className="align-items-center">
                        <Col>Qty</Col>
                        <Col>
                          <Form.Control
                            as="select"
                            value={qty}
                            onChange={(e) => setQty(Number(e.target.value))}
                          >
                            {qtyOptions.map((optionQty) => (
                              <option key={optionQty} value={optionQty}>
                                {optionQty}
                              </option>
                            ))}
                          </Form.Control>

                          {!isTieredUser && maxSelectableQty >= LIMIT && (
                            <small className="text-muted d-block mt-1">
                              Max {LIMIT} per item
                            </small>
                          )}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  )}

                  <ListGroup.Item>
                    {isPurchasable ? (
                      <Button
                        className="w-100"
                        type="button"
                        disabled={product.stockQty === 0}
                        onClick={addToCartHandler}
                      >
                        Add To Cart
                      </Button>
                    ) : (
                      <div className="text-center">
                        <Badge bg="warning" text="dark">Coming Soon</Badge>
                        <div className="small text-muted mt-2">
                          This product is not available for purchase yet.
                        </div>
                      </div>
                    )}
                  </ListGroup.Item>

                  {userInfo && userInfo.isAdmin && (
                    <ListGroup.Item>
                      <Link
                        to={`/admin/product/${product._id}/edit`}
                        className="btn btn-light w-100"
                      >
                        Edit Product
                      </Link>
                    </ListGroup.Item>
                  )}
                  {userInfo && userInfo.isAdmin && (
                    <ListGroup.Item>
                      <div className="small fw-semibold mb-1">Warehouse Locations</div>
                      <ProductLocationsSummary product={product} />
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </Card>
            </Col>
          </Row>

          {productSummary && (
            <Row className="mt-4">
              <Col xs={12}>
                <Card className="card-plain">
                  <Card.Body>
                    <h2 className="h5 mb-3">About this part</h2>
                    <p className="mb-0">{productSummary}</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {productAttributes.length > 0 && (
            <Row className="mt-4">
              <Col xs={12}>
                <Card className="card-plain">
                  <Card.Body>
                    <h2 className="h5 mb-3">Specifications</h2>
                    <Table responsive bordered size="sm" className="mb-0">
                      <tbody>
                        {productAttributes.map(([label, value]) => (
                          <tr key={label}>
                            <th scope="row" style={{ width: '30%' }}>
                              {isEngineOil && label === 'Specifications' ? 'Full Specification' : label}
                            </th>
                            <td>
                              {isEngineOil && label === 'OEM / Manufacturer Specifications' ? (
                                <div className="d-flex flex-wrap gap-2">
                                  {String(value).split(',').map((item) => item.trim()).filter(Boolean).map((item) => (
                                    <Badge key={item} bg="light" text="dark" className="border text-wrap">{item}</Badge>
                                  ))}
                                </div>
                              ) : value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* FULL WIDTH: FITMENT / INTERCHANGEABLE TABLES */}
          {(hasFitments || hasInterchangeables) && (
            <Row className="mt-4">
              <Col xs={12}>
                <Card className="product-fitment-card card-plain">
                  <Card.Body>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                      <div>
                        <div className="text-uppercase small text-muted fw-semibold">
                          Fitment Intelligence
                        </div>
                        <h5 className="mb-0">Fitment & Interchangeables</h5>
                      </div>
                      <ButtonGroup size="sm" className="fitment-toggle">
                        <Button
                          variant={fitmentView === "fitment" ? "primary" : "outline-primary"}
                          onClick={() => setFitmentView("fitment")}
                          disabled={!hasFitments}
                        >
                          Fitment{" "}
                          <Badge bg="light" text="dark" pill className="ms-1">
                            {fitmentRows.length}
                          </Badge>
                        </Button>
                        <Button
                          variant={
                            fitmentView === "interchangeable"
                              ? "primary"
                              : "outline-primary"
                          }
                          onClick={() => setFitmentView("interchangeable")}
                          disabled={!hasInterchangeables}
                        >
                          Interchangeables{" "}
                          <Badge bg="light" text="dark" pill className="ms-1">
                            {interchangeableRows.length}
                          </Badge>
                        </Button>
                      </ButtonGroup>
                    </div>

                    {fitmentView === "fitment" && hasFitments && (
                      <>
                        <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                          <span className="text-muted small">
                            {filteredFitments.length} of {fitmentRows.length}
                          </span>
                        </div>

                        <InputGroup className="mb-3 fitment-search">
                          <Form.Control
                            placeholder="Search fitments (toyota rav4 2018 le...)"
                            value={fitmentSearch}
                            onChange={(e) => setFitmentSearch(e.target.value)}
                          />
                          {fitmentSearch && (
                            <Button
                              variant="outline-secondary"
                              onClick={() => setFitmentSearch("")}
                            >
                              Clear
                            </Button>
                          )}
                        </InputGroup>

                        <div
                          style={{
                            maxHeight: 320,
                            overflowY: "auto",
                            overflowX: "auto",
                          }}
                        >
                          <Table
                            striped
                            hover
                            size="sm"
                            className="mb-0"
                            style={{ minWidth: 720 }}
                          >
                            <thead
                              style={{
                                position: "sticky",
                                top: 0,
                                background: "white",
                                zIndex: 1,
                              }}
                            >
                              <tr>
                                <th>Make</th>
                                <th>Model</th>
                                <th>Year</th>
                                <th>Trim</th>
                                <th>Engine</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredFitments.length === 0 ? (
                                <tr>
                                  <td colSpan={5} className="text-muted">
                                    No matching fitments.
                                  </td>
                                </tr>
                              ) : (
                                filteredFitments.map((r) => (
                                  <tr key={r.key}>
                                    <td>{r.make || "-"}</td>
                                    <td>{r.model || "-"}</td>
                                    <td>{r.year ?? "-"}</td>
                                    <td>{r.trim || "-"}</td>
                                    <td>{r.engine || "-"}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    )}

                    {fitmentView === "interchangeable" && hasInterchangeables && (
                      <div
                        style={{
                          maxHeight: 320,
                          overflowY: "auto",
                          overflowX: "auto",
                        }}
                      >
                        <Table striped hover size="sm" className="mb-0" style={{ minWidth: 520 }}>
                          <thead
                            style={{
                              position: "sticky",
                              top: 0,
                              background: "white",
                              zIndex: 1,
                            }}
                          >
                            <tr>
                              <th>Brand Label</th>
                              <th>Part Number</th>
                            </tr>
                          </thead>
                          <tbody>
                            {interchangeableRows.length === 0 ? (
                              <tr>
                                <td colSpan={2} className="text-muted">
                                  No interchangeables.
                                </td>
                              </tr>
                            ) : (
                              interchangeableRows.map((row) => (
                                <tr key={row.key}>
                                  <td>{row.brandLabel || "-"}</td>
                                  <td>{row.partNumber || "-"}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {!relatedProductsError && isLoadingRelated && (
            <section className="mt-5" aria-labelledby="related-products-heading">
              <h2 id="related-products-heading">{relatedProductsTitle}</h2>
              <Loader />
            </section>
          )}

          {!relatedProductsError && !isLoadingRelated && relatedProducts.length > 0 && (
            <RelatedProductsCarousel
              products={relatedProducts}
              title={relatedProductsTitle}
            />
          )}

          {/* Added to cart modal */}
          <Modal show={showCartModal} onHide={() => setShowCartModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Added to cart</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-3 align-items-center">
                <Col xs={12} md={4}>
                  <div className="border rounded bg-light d-flex align-items-center justify-content-center p-2">
                    <Image
                      src={product.image || activeImage}
                      alt={displayName}
                      fluid
                      style={{ maxHeight: 120, objectFit: "contain" }}
                    />
                  </div>
                </Col>
                <Col xs={12} md={8}>
                  <div className="fw-semibold">{displayName}</div>
                  <div className="text-muted mt-1">
                    Added <strong>{addedQty}</strong> item{addedQty > 1 ? "s" : ""}.
                  </div>
                  <div className="text-muted mt-2">
                    What would you like to do next?
                  </div>
                </Col>
              </Row>

              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-semibold">Cart snapshot</div>
                  <div className="text-muted small">
                    {cartPreview.totalQty} item{cartPreview.totalQty !== 1 ? "s" : ""}
                  </div>
                </div>
                <ListGroup variant="flush">
                  {cartPreview.previewItems.map((item) => (
                    <ListGroup.Item key={item._id || item.product} className="px-0">
                      <div className="d-flex align-items-center gap-3">
                        <Image
                          src={item.image}
                          alt={item.displayName || item.title || item.name}
                          rounded
                          width={44}
                          height={44}
                          style={{ objectFit: "cover" }}
                        />
                        <div className="flex-grow-1">
                          <div className="fw-semibold small">{item.displayName || item.title || item.name}</div>
                          <div className="text-muted small">
                            Qty {item.qty} ·{" "}
                            <PriceWithSale
                              price={Number(item.originalPrice ?? item.price ?? 0)}
                              salePrice={item.salePrice}
                              saleStartAt={item.saleStartAt}
                              saleEndAt={item.saleEndAt}
                              tierPrice={item.tierPriceApplied ? item.price : null}
                              showTierTag={Boolean(item.tierPriceApplied)}
                              forceBasePrice={Boolean(item.ignoreSale && !item.tierPriceApplied)}
                            />
                          </div>
                        </div>
                        <div className="fw-semibold small">
                          ${(
                            Number(item.qty || 0) * getCartItemUnitPrice(item)
                          ).toFixed(2)}
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
                {cartPreview.remainingCount > 0 && (
                  <div className="text-muted small mt-2">
                    + {cartPreview.remainingCount} more item
                    {cartPreview.remainingCount !== 1 ? "s" : ""} in your cart
                  </div>
                )}
                <div className="d-flex justify-content-between align-items-center mt-3 border-top pt-3">
                  <div className="text-muted">Subtotal</div>
                  <div className="fw-semibold">
                    ${cartPreview.itemsPrice.toFixed(2)}
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => setShowCartModal(false)}
              >
                Continue Shopping
              </Button>
              <Button variant="primary" onClick={goToCart}>
                Go to Cart
              </Button>
            </Modal.Footer>
          </Modal>
          <Modal
            show={showLimitModal}
            onHide={() => setShowLimitModal(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Large quantity request</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Orders over {LIMIT} units qualify for additional discounts. Please{" "}
              <Link to="/contact">contact us</Link> and call to place a larger
              order.
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => setShowLimitModal(false)}
              >
                Close
              </Button>
              <Button
                as={Link}
                to="/contact"
                variant="primary"
                onClick={() => setShowLimitModal(false)}
              >
                Contact Us
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </>
  );
};

export default ProductScreen;
